import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import StatusBadge from '../../components/common/StatusBadge';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as ticketsApi from '../../api/tickets';
import * as employeesApi from '../../api/employees';
import { formatDateTime } from '../../utils/format';

const CATEGORY_OPTIONS = ['Project Issue', 'Missing Documents', 'Approval Request', 'Technical Support', 'HR Request', 'Other'].map((v) => ({ value: v, label: v }));
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'].map((v) => ({ value: v, label: v }));
const STATUS_OPTIONS = ['Open', 'In Progress', 'Resolved', 'Closed'].map((v) => ({ value: v, label: v }));

const emptyForm = { subject: '', description: '', category: 'Other', priority: 'Medium' };

export default function Tickets() {
  const { actor } = useAuth();
  const canManage = actor.role === 'super_admin' || actor.role === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    ticketsApi
      .listTickets({})
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load tickets.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await ticketsApi.createTicket(form);
      setSuccess('Ticket raised.');
      setShowForm(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to raise ticket.');
    }
  };

  const openDetail = async (row) => {
    const res = await ticketsApi.getTicket(row.id);
    setSelectedTicket(res.data);
    if (canManage && employeeOptions.length === 0) {
      const empRes = await employeesApi.listEmployees({ limit: 500 });
      setEmployeeOptions(empRes.data.map((e) => ({ value: e.id, label: e.name })));
    }
  };

  const refreshDetail = async () => {
    const res = await ticketsApi.getTicket(selectedTicket.id);
    setSelectedTicket(res.data);
  };

  const handleStatusUpdate = async (status) => {
    try {
      await ticketsApi.updateTicket(selectedTicket.id, { status });
      await refreshDetail();
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update ticket.');
    }
  };

  const handleAssign = async (assignedTo) => {
    try {
      await ticketsApi.updateTicket(selectedTicket.id, { assignedTo });
      await refreshDetail();
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign ticket.');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      await ticketsApi.addTicketComment(selectedTicket.id, commentText.trim());
      setCommentText('');
      await refreshDetail();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add comment.');
    }
  };

  const columns = [
    { key: 'subject', label: 'Subject', render: (r) => <button type="button" className="btn-link" onClick={() => openDetail(r)}>{r.subject}</button> },
    { key: 'category', label: 'Category' },
    { key: 'priority', label: 'Priority', render: (r) => <StatusBadge status={r.priority} /> },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'raiser', label: 'Raised By', render: (r) => r.raiser?.name || '-' },
    { key: 'assignee', label: 'Assigned To', render: (r) => r.assignee?.name || 'Unassigned' },
  ];

  return (
    <DashboardLayout title="Internal Tickets">
      <div className="page-header">
        <div>
          <h1 className="heading">Internal Tickets</h1>
          <p className="page-subtext">Issues, approvals and requests raised by staff.</p>
        </div>
        <button type="button" className="btn" onClick={() => { setForm(emptyForm); setShowForm(true); }}>Raise Ticket</button>
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      {loading ? <Loader text="Loading tickets..." /> : <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No tickets found." />}

      {showForm && (
        <Modal
          title="Raise a Ticket"
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="ticket-form" className="btn">Submit</button>
            </>
          }
        >
          <form id="ticket-form" onSubmit={handleCreate}>
            <div className="form-grid single-column">
              <FormField label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
              <FormField label="Description" type="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <FormField label="Category" type="select" options={CATEGORY_OPTIONS} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <FormField label="Priority" type="select" options={PRIORITY_OPTIONS} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
            </div>
          </form>
        </Modal>
      )}

      {selectedTicket && (
        <Modal title={selectedTicket.subject} onClose={() => setSelectedTicket(null)} width={700}>
          <p className="text-muted">{selectedTicket.category} &middot; Raised by {selectedTicket.raiser?.name}</p>
          <p>{selectedTicket.description || 'No description.'}</p>

          <div className="btn-group" style={{ marginBottom: 12 }}>
            {STATUS_OPTIONS.map((s) => (
              <button key={s.value} type="button" className={s.value === selectedTicket.status ? 'btn btn-small' : 'btn btn-secondary btn-small'} onClick={() => handleStatusUpdate(s.value)}>
                {s.label}
              </button>
            ))}
          </div>

          {canManage && (
            <div className="form-field" style={{ maxWidth: 320 }}>
              <label>Assign To</label>
              <select value={selectedTicket.assignee?.id || ''} onChange={(e) => handleAssign(e.target.value)}>
                <option value="">Unassigned</option>
                {employeeOptions.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </div>
          )}

          <h3 className="subheading">Comments</h3>
          <div className="comment-list">
            {(selectedTicket.comments || []).map((c) => (
              <div key={c.id} className="comment-item">
                <div className="comment-meta">{c.author?.name} &middot; {formatDateTime(c.createdAt)}</div>
                <div>{c.comment}</div>
              </div>
            ))}
            {(!selectedTicket.comments || selectedTicket.comments.length === 0) && <p className="text-muted">No comments yet.</p>}
          </div>
          <form onSubmit={handleAddComment} style={{ display: 'flex', gap: 10 }}>
            <input type="text" placeholder="Add a comment..." value={commentText} onChange={(e) => setCommentText(e.target.value)} />
            <button type="submit" className="btn btn-small">Post</button>
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}
